import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Gift, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "./ui/dialog";

const TERMS_BULLETS = [
  "Spend $1,000 within 60 days of creating your account",
  "Reach the right customers with hyperlocal targeting",
  "Drive more store visits and real business results",
  "Powerful location-based advertising included"
];

/**
 * Credit Offer Modal — shows full $500 credit terms when triggered.
 * Wire to any "Claim your $500" / "Get $500 credit" CTA across the site.
 */
export const CreditOfferModal = ({ open, onOpenChange }) => {
  const navigate = useNavigate();

  const handleClaim = () => {
    onOpenChange(false);
    // Pass source so the contact form can pre-fill subject + flag the lead
    navigate("/contact", { state: { source: "credit-claim" } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0"
        data-testid="credit-offer-modal"
      >
        {/* Orange header */}
        <div className="bg-gradient-to-br from-orange-primary via-orange-secondary to-orange-primary p-8 text-white text-center relative">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            aria-label="Close"
            data-testid="credit-modal-close-x"
          >
            <X size={20} />
          </button>
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-4">
            <Gift size={32} />
          </div>
          <DialogHeader className="space-y-1">
            <DialogTitle className="font-display text-3xl font-bold text-center">
              Get $500 To Kickstart Your Campaign
            </DialogTitle>
            <DialogDescription className="text-white/90 text-base text-center">
              On us, when you spend $1,000 in your first 60 days.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="p-8 bg-white">
          <p className="text-gray-dark mb-5 leading-relaxed">
            Sign up for Ads Manager and receive a <strong className="text-orange-primary">$500 credit</strong> after
            you spend $1,000 within 60 days of creating your account. Reach the right customers,
            drive more store visits, and see real business results. Don't miss this opportunity to
            grow your business with powerful location-based advertising.
          </p>

          <ul className="space-y-3 mb-6">
            {TERMS_BULLETS.map((t) => (
              <li key={t} className="flex items-start gap-3" data-testid={`credit-terms-${t.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-5 h-5 rounded-full bg-orange-light flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={12} className="text-orange-primary" />
                </div>
                <span className="text-sm text-gray-dark">{t}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={handleClaim}
            className="w-full btn-primary text-lg flex items-center justify-center gap-2"
            data-testid="credit-modal-claim-btn"
          >
            Claim Your Credit Now <ArrowRight size={20} />
          </button>

          <p className="text-xs text-gray-medium text-center mt-4">
            * No contracts. No minimum spend. Cancel anytime.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreditOfferModal;
